import { useEffect } from "react";
import { t } from "@/i18n";
import {
  useSessionStore,
  type SessionInvalidReason,
} from "@/stores/sessionStore";
import { useAuthStore } from "@/stores/authStore";
import { authService } from "@/services/auth";

/**
 * Razão genérica de expiração. Com autenticação por token (api_key:api_secret)
 * permanente e compartilhado entre logins do mesmo usuário, não há como
 * distinguir "login concorrente" de "limite de licença" como no modelo de
 * cookie (sid=guest) — então usamos uma única mensagem.
 */
const GENERIC_EXPIRED_REASON: SessionInvalidReason = {
  type: "unknown",
  message: t("auth.sessionExpired"),
};

/**
 * Programmatic trigger — chamado pelo interceptor 401/403 do httpClient quando
 * o servidor sinaliza token inválido/revogado. Seta a flag compartilhada para
 * que o SessionExpiredModal (montado em MainLayout) renderize.
 */
export function triggerSessionExpired(reason?: SessionInvalidReason): void {
  useSessionStore.getState().triggerExpired(reason ?? GENERIC_EXPIRED_REASON);
}

export function clearSessionExpired(): void {
  useSessionStore.getState().reset();
}

/**
 * Heartbeat leve: a cada `intervalMs` valida o token no servidor via
 * `frappe.auth.get_logged_user`. Se o token foi revogado (retorna "Guest"),
 * dispara o modal de sessão expirada e faz logout. É um backstop — o caminho
 * principal de detecção é reativo, pelos interceptors 401/403 do httpClient.
 *
 * Default de 60s (padrão MaxFactory): cada verificação é uma chamada de rede,
 * então evita-se intervalos curtos. Deve ser montado uma única vez no topo da
 * árvore autenticada.
 */
export function useSessionMonitoring(intervalMs: number = 60000): void {
  useEffect(() => {
    const id = window.setInterval(async () => {
      if (!useAuthStore.getState().user) return;
      const valid = await authService.validateToken();
      if (!valid) {
        triggerSessionExpired();
        await useAuthStore.getState().logout();
      }
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [intervalMs]);
}
